<template>
    <div class="edit-page">
        <div v-if="loadError" class="sheet">
            <header class="flow-head">
                <router-link class="icon-btn" to="/settings/users" aria-label="Voltar">
                    <img src="images/icons/arrow-left.svg" alt="" width="20" height="20" />
                </router-link>
                <h1>{{ t('users-edit-title') }}</h1>
                <span class="head-spacer" aria-hidden="true"></span>
            </header>
            <div class="state">
                <span class="state-icon alert"><img src="images/icons/alert-triangle.svg" alt="" /></span>
                <h2>{{ t('users-edit-load_error_title') }}</h2>
                <p>{{ t('users-edit-load_error_body') }}</p>
                <div class="state-actions">
                    <router-link class="btn-ghost" to="/settings/users">{{ t('users-edit-back') }}</router-link>
                    <button type="button" class="btn-green" @click="loadUser">{{ t('users-reload') }}</button>
                </div>
            </div>
        </div>

        <div v-else-if="!user" class="sheet">
            <header class="flow-head">
                <router-link class="icon-btn" to="/settings/users" aria-label="Voltar">
                    <img src="images/icons/arrow-left.svg" alt="" width="20" height="20" />
                </router-link>
                <h1>{{ t('users-edit-title') }}</h1>
                <span class="head-spacer" aria-hidden="true"></span>
            </header>
            <div class="state">
                <span class="spinner" aria-hidden="true"></span>
                <p>{{ t('users-loading') }}</p>
            </div>
        </div>

        <div v-else-if="user" class="sheet">
            <header class="flow-head">
                <router-link class="icon-btn" to="/settings/users" aria-label="Voltar">
                    <img src="images/icons/arrow-left.svg" alt="" width="20" height="20" />
                </router-link>
                <h1>{{ title }}</h1>
                <span class="head-spacer" aria-hidden="true"></span>
            </header>

            <div class="who-chip">
                <img v-if="user.logo" :src="user.logo" alt="" />
                <span v-else class="who-initials">{{ initials(user.name) }}</span>
                <div class="who-copy">
                    <strong>{{ user.name }}</strong>
                    <small>@{{ user.username }}</small>
                </div>
            </div>

            <transition name="pane" mode="out-in">
                <form v-if="section === 'profile'" key="profile" class="edit-body" @submit.prevent="askContactOrSave">
                    <div class="photo">
                        <img v-if="form.logo" :src="form.logo" alt="" />
                        <span v-else class="ph-big">{{ initials(user.name) }}</span>
                        <button type="button" class="btn-photo" @click="$refs.photo.click()">{{ t('users-create-photo_change') }}</button>
                        <input ref="photo" class="sr" type="file" accept="image/png,image/jpeg,image/webp,image/gif" @change="uploadPhoto" />
                    </div>

                    <label class="field">
                        <span>{{ t('users-create-name') }}</span>
                        <input v-model="form.name" required autocomplete="name" />
                    </label>
                    <label class="field">
                        <span>{{ t('users-create-username') }}</span>
                        <input v-model="form.username" required autocomplete="username" @input="onUsernameInput" />
                    </label>
                    <label class="field">
                        <span>{{ t('users-create-email') }}</span>
                        <div class="field-input">
                            <img src="images/icons/mail-02.svg" alt="" class="field-icon" />
                            <input v-model="form.email" type="email" :placeholder="t('users-create-email_placeholder')" autocomplete="email" />
                        </div>
                    </label>
                    <label class="field">
                        <span>{{ t('users-create-phone') }}</span>
                        <div class="field-input">
                            <img src="images/icons/phone.svg" alt="" class="field-icon" />
                            <input v-model="form.phone" type="tel" :placeholder="t('users-create-phone_placeholder')" autocomplete="tel" />
                        </div>
                    </label>

                    <label v-if="branches.length > 1" class="field">
                        <span>Filial</span>
                        <select v-model="form.places_branches_id">
                            <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.title }}</option>
                        </select>
                    </label>

                    <kv-switch
                        v-if="isGlobalAdmin"
                        v-model="form.global_admin"
                        :label="form.global_admin ? 'Admin global — Sim' : 'Admin global — Não'"
                    />

                    <kv-switch
                        v-if="isGlobalAdmin"
                        v-model="form.is_internal"
                        :label="form.is_internal ? 'Interno — Sim' : 'Interno — Não'"
                    />

                    <div v-if="isGlobalAdmin" class="product-picks">
                        <kv-switch
                            v-model="keviMarketingEnabled"
                            :label="keviMarketingEnabled ? 'Kevi MKT — Ativar' : 'Kevi MKT — Não ativar'"
                        />
                        <kv-switch
                            v-model="keviChatEnabled"
                            :label="keviChatEnabled ? 'Kevi Chat — Ativar' : 'Kevi Chat — Não ativar'"
                        />
                    </div>
                    <p v-if="error" class="hint warn">{{ error }}</p>
                    <div class="footer">
                        <router-link class="btn-ghost" to="/settings/users">{{ t('users-edit-back') }}</router-link>
                        <button class="btn-green" type="submit">
                            {{ t('users-edit-save') }}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.2 2.6 2.6 6.4-6.6" stroke="#F1FCF3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                    </div>
                </form>

                <form v-else-if="section === 'permissions'" key="permissions" class="edit-body" @submit.prevent="openPassword">
                    <div class="section-head">
                        <div class="section-badge">
                            <img src="images/icons/shield-tick-green.svg" alt="" />
                        </div>
                        <h2>{{ t('users-create-permissions_title') }}</h2>
                        <p>{{ t('users-create-permissions_subtitle') }}</p>
                    </div>
                    <p v-if="protectedTarget" class="hint warn">{{ t('users-deactivate-protected') }}</p>
                    <permissions-editor :permissions="permissions" :locked="lockedPermissions" />
                    <p v-if="error" class="hint warn">{{ error }}</p>
                    <div class="footer">
                        <router-link class="btn-ghost" to="/settings/users">{{ t('users-edit-back') }}</router-link>
                        <button class="btn-green" type="submit">{{ t('users-edit-apply') }}</button>
                    </div>
                </form>

                <form v-else key="password" class="edit-body" @submit.prevent="askPassword">
                    <div class="section-head password-head">
                        <div class="section-badge">
                            <img src="images/icons/key-01-green.svg" alt="" />
                        </div>
                        <h2>{{ t('users-edit-password') }}</h2>
                        <p>{{ t('users-profile-password_rule') }}</p>
                    </div>
                    <label class="field">
                        <span>{{ t('users-edit-new_password') }}</span>
                        <div class="secret-input">
                            <input
                                v-model="password"
                                :type="showPassword ? 'text' : 'password'"
                                minlength="8"
                                required
                                autocomplete="new-password"
                                placeholder="****************"
                            />
                            <button type="button" class="eye-btn" @click="showPassword = !showPassword" :aria-label="showPassword ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                    </label>
                    <label class="field">
                        <span>{{ t('users-edit-repeat') }}</span>
                        <div class="secret-input">
                            <input
                                v-model="passwordConfirmation"
                                :type="showPasswordRepeat ? 'text' : 'password'"
                                minlength="8"
                                required
                                autocomplete="new-password"
                                placeholder="****************"
                            />
                            <button type="button" class="eye-btn" @click="showPasswordRepeat = !showPasswordRepeat" :aria-label="showPasswordRepeat ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                    </label>
                    <p v-if="error" class="hint warn">{{ error }}</p>
                    <div class="footer">
                        <router-link class="btn-ghost" to="/settings/users">{{ t('users-edit-back') }}</router-link>
                        <button class="btn-green" type="submit">
                            {{ t('users-edit-change_password') }}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3.5 8.2 2.6 2.6 6.4-6.6" stroke="#F1FCF3" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </button>
                    </div>
                </form>
            </transition>
        </div>

        <modal ref="contactModal" size="small" :show-close="false" class="edit-modal-host">
            <template slot="body">
                <div class="edit-modal-flow">
                    <div class="edit-modal-head">
                        <div class="section-badge">
                            <img src="images/icons/alert-triangle.svg" alt="" />
                        </div>
                        <h2>{{ t('users-edit-contact_title') }}</h2>
                        <p>{{ t('users-edit-contact_body') }}</p>
                    </div>
                    <div class="edit-modal-footer">
                        <button type="button" class="btn-ghost" @click="$refs.contactModal.close()">{{ t('users-edit-contact_back') }}</button>
                        <button type="button" class="btn-green" @click="openPassword">{{ t('users-edit-contact_continue') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <modal ref="sessionsModal" size="small" :show-close="false" class="edit-modal-host">
            <template slot="body">
                <div class="edit-modal-flow">
                    <div class="edit-modal-head">
                        <div class="section-badge">
                            <img src="images/icons/log-out-01.svg" alt="" />
                        </div>
                        <h2>{{ t('users-edit-sessions_title') }}</h2>
                        <p>{{ t('users-edit-sessions_body') }}</p>
                    </div>
                    <div class="edit-modal-footer">
                        <button type="button" class="btn-ghost" :disabled="passwordSubmitting" @click="savePassword(false)">{{ t('users-edit-keep_sessions') }}</button>
                        <button type="button" class="btn-green" :disabled="passwordSubmitting" @click="savePassword(true)">{{ t('users-edit-end_sessions') }}</button>
                    </div>
                </div>
            </template>
        </modal>

        <modal ref="passwordModal" size="small" :show-close="false" class="edit-modal-host">
            <template slot="body">
                <div class="edit-modal-flow">
                    <div class="edit-modal-head">
                        <div class="section-badge is-blue">
                            <img src="images/icons/shield-blue.svg" alt="" />
                        </div>
                        <h2>{{ t('users-password_modal-title') }}</h2>
                        <p>{{ t('users-password_modal-subtitle') }}</p>
                    </div>
                    <label class="field">
                        <span class="sr-label">{{ t('users-password_modal-label') }}</span>
                        <div class="secret-input">
                            <input
                                v-model="adminPassword"
                                :placeholder="t('users-password_modal-label')"
                                :type="showAdminPassword ? 'text' : 'password'"
                                :disabled="passwordSubmitting"
                                autocomplete="current-password"
                                @keyup.enter="continueAfterPassword"
                            />
                            <button type="button" class="eye-btn" @click="showAdminPassword = !showAdminPassword" :aria-label="showAdminPassword ? t('users-edit-hide') : t('users-edit-show')">
                                <img src="images/icons/eye.svg" alt="" />
                            </button>
                        </div>
                    </label>
                    <p v-if="error" class="hint warn">{{ error }}</p>
                    <div class="edit-modal-footer">
                        <button type="button" class="btn-ghost" :disabled="passwordSubmitting" @click="$refs.passwordModal.close()">{{ t('users-password_modal-close') }}</button>
                        <button type="button" class="btn-green" :disabled="passwordSubmitting || !(adminPassword || '').trim()" @click="continueAfterPassword">
                            <span v-if="passwordSubmitting" class="fa fa-spinner fa-spin" aria-hidden="true"></span>
                            {{ passwordSubmitting ? t('users-password_modal-submitting') : t('users-password_modal-continue') }}
                        </button>
                    </div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import PermissionsEditor from "./permissions-editor.vue";
import { uploadFilesToStorage } from "../utils/upload-helper";
import KvSwitch from "../components/inputs/kv-switch.vue";

export default {
    name: "team-user-edit",
    components: { PermissionsEditor, "kv-switch": KvSwitch },
    props: { section: { type: String, default: "profile" } },
    data() {
        return {
            user: null,
            loadError: "",
            keviMarketingEnabled: true,
            keviChatEnabled: false,
            form: {
                name: "",
                username: "",
                email: "",
                phone: "",
                logo: null,
                places_branches_id: null,
                global_admin: false,
                is_internal: false,
            },
            originalContact: { email: "", phone: "" },
            permissions: {},
            password: "",
            passwordConfirmation: "",
            showPassword: false,
            showPasswordRepeat: false,
            showAdminPassword: false,
            adminPassword: "",
            passwordSubmitting: false,
            error: "",
        };
    },
    computed: {
        branches() {
            const branches = (this.$store.state.place && this.$store.state.place.branches) || [];
            return (branches || []).filter((b) => Number(b.active ?? 1) === 1);
        },
        isGlobalAdmin() {
            const user = this.$store.state.user || {};
            return (user.global_admin === true || user.global_admin === 1);
        },
        protectedTarget() {
            return !!(this.user && this.user.blocked_actions && this.user.blocked_actions.users_permission === "protected");
        },
        lockedPermissions() {
            const reason = this.user && this.user.blocked_actions && this.user.blocked_actions.users_permission;
            if (!reason) return {};
            const map = {
                self: "users-deactivate-self_permission",
                protected: "users-deactivate-protected",
                last_admin: "users-deactivate-last_admin",
            };
            return { "account.users.manage": this.t(map[reason] || "users-deactivate-protected") };
        },
        title() {
            if (this.section === "permissions") return this.t("users-edit-permissions");
            if (this.section === "password") return this.t("users-edit-password");
            return this.t("users-edit-title");
        },
    },
    created() {
        this.loadUser();
    },
    watch: {
        "$route.params.id"() {
            this.loadUser();
        },
        section() {
            this.error = "";
            this.showPassword = false;
            this.showPasswordRepeat = false;
        },
    },
    methods: {
        stripUsernameWhitespace(value) {
            return String(value || "").replace(/\s+/g, "");
        },
        onUsernameInput() {
            const stripped = this.stripUsernameWhitespace(this.form.username || "");
            if (stripped !== (this.form.username || "")) {
                this.form.username = stripped;
            }
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        loadUser() {
            const id = String(this.$route.params.id || "");
            this.user = null;
            this.loadError = "";
            this.$http(keviApi.getOptions("place/team-users/" + encodeURIComponent(id))).then(
                (response) => {
                    const body = response.body || {};
                    if (!body.name && !body.username) {
                        this.loadError = this.t("users-load_error");
                        return;
                    }
                    body.id = id;
                    this.user = body;
                    this.keviMarketingEnabled = (body.kevi_marketing_enabled === true || body.kevi_marketing_enabled === 1);
                    this.keviChatEnabled = (body.kevi_chat_enabled === true || body.kevi_chat_enabled === 1);
                    this.form = {
                        name: body.name || "",
                        username: body.username || "",
                        email: body.email || "",
                        phone: body.phone || "",
                        logo: body.logo || null,
                        places_branches_id: body.places_branches_id || null,
                        global_admin: (body.global_admin === true || body.global_admin === 1),
                        is_internal: (body.is_internal === true || body.is_internal === 1),
                    };
                    this.originalContact = { email: this.form.email, phone: this.form.phone };
                    const permissions = {};
                    const current = body.permissions || {};
                    Object.keys(current).forEach((name) => {
                        const options = current[name] && current[name].options;
                        permissions[name] = {
                            enabled: current[name] && current[name].enabled ? 1 : 0,
                            options: options && typeof options === "object" ? options : {},
                        };
                    });
                    ["account.users.manage"].forEach((name) => {
                        if (!permissions[name]) permissions[name] = { enabled: 0, options: {} };
                    });
                    this.permissions = permissions;
                },
                () => {
                    this.loadError = this.t("users-load_error");
                }
            );
        },
        initials(name) {
            return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
        },
        async uploadPhoto(event) {
            const input = event.target;
            const file = input.files && input.files[0];
            if (!file) return;
            try {
                const urls = await uploadFilesToStorage([file], this.t.bind(this));
                if (urls && urls[0]) this.form.logo = urls[0];
            } catch (err) {
                this.error = (err && err.message) || this.t("users-profile-photo_failed");
            } finally {
                if (input) input.value = "";
            }
        },
        askContactOrSave() {
            const changed = (this.form.email || "") !== (this.originalContact.email || "")
                || (this.form.phone || "") !== (this.originalContact.phone || "");
            // Qualquer contato novo ou alterado volta a "não verificado": avisa antes de pedir a senha.
            if (changed && (this.form.email || this.form.phone)) {
                this.$refs.contactModal.open();
                return;
            }
            this.openPassword();
        },
        openPassword() {
            if (this.$refs.contactModal) this.$refs.contactModal.close();
            this.error = "";
            this.adminPassword = "";
            this.showAdminPassword = false;
            this.passwordSubmitting = false;
            this.$refs.passwordModal.open();
        },
        askPassword() {
            // Valida antes de pedir a senha do admin, para o erro aparecer no próprio formulário.
            if ((this.password || "").length < 8) {
                this.error = this.t("users-profile-password_rule");
                return;
            }
            if (this.password !== this.passwordConfirmation) {
                this.error = this.t("users-profile-mismatch");
                return;
            }
            this.openPassword();
        },
        continueAfterPassword() {
            if (this.passwordSubmitting) return;
            if (!(this.adminPassword || "").trim()) return;
            if (this.section === "password") {
                this.$refs.passwordModal.close();
                this.$refs.sessionsModal.open();
                return;
            }
            if (this.section === "permissions") {
                this.savePermissions();
                return;
            }
            this.saveProfile();
        },
        saveProfile() {
            if (this.passwordSubmitting) return;
            this.passwordSubmitting = true;
            this.error = "";
            this.onUsernameInput();
            const { global_admin, is_internal, ...common } = this.form;
            const payload = {
                ...common,
                ...(this.isGlobalAdmin
                    ? {
                          kevi_marketing_enabled: this.keviMarketingEnabled ? 1 : 0,
                          kevi_chat_enabled: this.keviChatEnabled ? 1 : 0,
                          global_admin: global_admin ? 1 : 0,
                          is_internal: is_internal ? 1 : 0,
                      }
                    : {}),
                admin_password: this.adminPassword,
            };
            const options = keviApi.getOptions(
                "place/team-users/" + encodeURIComponent(String(this.user.id)) + "/profile",
                "POST",
                payload
            );
            this.$http(options).then(
                () => {
                    this.passwordSubmitting = false;
                    this.$store.dispatch("notification/success", this.t("users-edit-saved"));
                    this.$router.push("/settings/users");
                },
                (response) => {
                    this.passwordSubmitting = false;
                    this.error = this.errorText(response);
                }
            );
        },
        savePermissions() {
            if (this.passwordSubmitting) return;
            this.passwordSubmitting = true;
            this.error = "";
            const options = keviApi.getOptions("place/team-users/" + encodeURIComponent(String(this.user.id)) + "/permissions", "POST", {
                permissions: this.permissions,
                admin_password: this.adminPassword,
            });
            this.$http(options).then(
                () => {
                    this.passwordSubmitting = false;
                    this.$store.dispatch("notification/success", this.t("users-edit-saved"));
                    this.$router.push("/settings/users");
                },
                (response) => {
                    this.passwordSubmitting = false;
                    this.error = this.errorText(response);
                }
            );
        },
        savePassword(logout) {
            if (this.passwordSubmitting) return;
            this.passwordSubmitting = true;
            this.error = "";
            const options = keviApi.getOptions("place/team-users/" + encodeURIComponent(String(this.user.id)) + "/password", "POST", {
                password: this.password,
                password_confirmation: this.passwordConfirmation,
                admin_password: this.adminPassword,
                logout_sessions: logout,
            });
            this.$http(options).then(
                () => {
                    this.passwordSubmitting = false;
                    this.$store.dispatch("notification/success", this.t(logout ? "users-edit-toast_ended" : "users-edit-toast_kept"));
                    this.$router.push("/settings/users");
                },
                (response) => {
                    this.passwordSubmitting = false;
                    this.$refs.sessionsModal.close();
                    this.$refs.passwordModal.open();
                    this.error = this.errorText(response);
                }
            );
        },
        errorText(response) {
            const body = response.body || {};
            const code = body.message;
            // Mensagens "humanas" (ex.: limite de usuários) devem aparecer direto na UI.
            if (response && response.status === 409 && typeof code === "string" && code.trim().length > 10 && !body.field) {
                return code;
            }
            if (response.status === 409 && body.field) {
                return this.t("users-edit-taken_field", { 0: this.t("users-create-" + body.field) });
            }
            const map = {
                wrong_password: "users-password_modal-wrong",
                too_many_attempts: "users-password_modal-too_many",
                mismatch: "users-profile-mismatch",
                weak_password: "users-profile-password_rule",
                self: "users-deactivate-self",
                protected: "users-deactivate-protected",
                forbidden_target: "users-deactivate-protected",
                last_admin: "users-deactivate-last_admin",
                taken: "users-profile-taken",
            };
            if (map[code]) return this.t(map[code]);
            if (response.status === 429) return this.t("users-password_modal-too_many");
            return this.t("users-password_modal-failed");
        },
    },
};
</script>

<style scoped>
.edit-page {
    min-height: calc(100dvh - 88px);
    background: #f7f7f8;
    display: flex;
    justify-content: center;
    align-items: stretch;
    margin: -20px -20px 0;
    padding: 16px 24px;
    box-sizing: border-box;
    font-family: "Inter", sans-serif;
    animation: rise .28s cubic-bezier(.16, 1, .3, 1);
}
@keyframes rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
.sheet {
    width: min(640px, 100%);
    min-height: calc(100dvh - 120px);
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
}
.flow-head {
    display: grid;
    grid-template-columns: 36px 1fr 36px;
    align-items: center;
    margin: 12px 12px 8px;
}
.flow-head h1 {
    margin: 0;
    text-align: center;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    color: #626473;
}
.head-spacer { width: 36px; height: 36px; }
.icon-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    text-decoration: none;
}
.icon-btn:hover { background: #f3f3f6; }
.who-chip {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 16px 8px;
    padding: 10px 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    background: #fafafc;
}
.who-chip img,
.who-initials {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background: #f3f3f6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #626473;
    font-size: 14px;
    font-weight: 500;
    flex: none;
}
.who-copy { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.who-copy strong { font-size: 14px; font-weight: 500; color: #1f2337; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.who-copy small { font-size: 12px; color: #626473; overflow-wrap: anywhere; word-break: break-word; }
.edit-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 16px 0;
    flex: 1;
    min-height: 0;
    overflow: auto;
}
.section-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 8px;
    margin-bottom: 4px;
}
.section-badge {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    background: #d3f8e0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.section-badge img { width: 20px; height: 20px; display: block; }
.section-badge.is-blue { background: #f0f5ff; }
.section-badge.is-blue img { width: 24px; height: 24px; }
.sr-label { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); border: 0; }
.section-head h2 {
    margin: 0;
    font-family: "Sora", sans-serif;
    font-size: 18px;
    font-weight: 400;
    color: #1f2337;
    line-height: 1.3;
}
.section-head p {
    margin: 0;
    max-width: 360px;
    color: #626473;
    font-size: 14px;
    line-height: 1.3;
}
.field { display: flex; flex-direction: column; gap: 8px; margin: 0; }
.field > span { font-size: 14px; font-weight: 500; color: #383c50; }
.field > input,
.field > select,
.field-input,
.secret-input {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
    height: 44px;
    margin: 0;
    padding: 0 12px;
    border: 1px solid #e5e6eb;
    border-radius: 8px;
    background: #f9f9fb;
    box-shadow: none;
    font-family: "Inter", sans-serif;
    font-size: 12px;
    line-height: 1.2;
    color: #1f2337;
    gap: 6px;
}
.field > input,
.field > select { display: block; }
.field > input:focus,
.field > select:focus,
.field-input:focus-within,
.secret-input:focus-within {
    outline: none;
    border-color: #bbbdc7;
    background: #fff;
}
.field-input input,
.secret-input input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
    font: inherit;
    color: inherit;
    outline: none;
}
.field-icon { width: 20px; height: 20px; flex: none; }
.product-picks {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    border: 1px solid #e5e6eb;
    border-radius: 12px;
    background: #fafafc;
}
.eye-btn {
    border: 0;
    background: transparent;
    padding: 0;
    width: 20px;
    height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
}
.eye-btn img { width: 20px; height: 20px; display: block; }
.photo { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.photo img,
.ph-big {
    width: 124px;
    height: 124px;
    border-radius: 50%;
    object-fit: cover;
    background: #f3f3f6;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #626473;
    font-size: 28px;
    font-weight: 500;
}
.btn-photo {
    height: 35px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid #e5e6eb;
    background: #fafafc;
    color: #383c50;
    font-size: 14px;
    cursor: pointer;
}
.sr { display: none; }
.footer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: auto -16px 0;
    position: sticky;
    bottom: 0;
    z-index: 2;
    padding: 16px;
    border-top: 1px solid #e5e6eb;
    background: #fafafc;
    box-sizing: border-box;
}
.btn-ghost,
.btn-green {
    flex: 1;
    min-height: 44px;
    box-sizing: border-box;
    padding: 12px;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.2;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-decoration: none;
    cursor: pointer;
}
.btn-ghost { border: 1px solid #e5e6eb; background: #fafafc; color: #383c50; }
.btn-green { border: 0; background: #2aaa4c; color: #f1fcf3; }
.btn-green:disabled,
.btn-ghost:disabled { opacity: .65; cursor: default; }
.btn-green svg { flex: none; }
.hint { margin: 0; font-size: 13px; }
.hint.warn { color: #b54708; }
.pad { padding: 0 16px 16px; }
.state {
    flex: 1;
    min-height: 280px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 32px 24px 48px;
    text-align: center;
}
.state h2 {
    margin: 8px 0 0;
    font-family: "Sora", sans-serif;
    font-size: 20px;
    font-weight: 400;
    line-height: 1.3;
    color: #1f2337;
}
.state p {
    margin: 0;
    max-width: 320px;
    font-size: 14px;
    line-height: 1.35;
    color: #626473;
}
.state-icon {
    width: 40px;
    height: 40px;
    border-radius: 100px;
    background: #edfcf3;
    display: flex;
    align-items: center;
    justify-content: center;
}
.state-icon.alert { background: #fffbed; }
.state-icon img { width: 24px; height: 24px; display: block; }
.state-actions { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.state-actions .btn-ghost,
.state-actions .btn-green { text-decoration: none; }
.spinner {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid #d9e5ff;
    border-top-color: #2f6bff;
    animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.pane-enter-active, .pane-leave-active { transition: opacity .2s ease, transform .24s cubic-bezier(.16, 1, .3, 1); }
.pane-enter { opacity: 0; transform: translateY(10px); }
.pane-leave-to { opacity: 0; transform: translateY(-8px); }

.edit-modal-host >>> .kv-modal-header,
.edit-modal-host >>> .kv-modal-footer { display: none; }
.edit-modal-host >>> .kv-modal-wrapper.kv-modal-small { max-width: 496px; }
.edit-modal-host >>> .kv-modal-dialog {
    background: #fff;
    border: 1px solid #e5e6eb;
    border-radius: 16px;
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.08);
}
.edit-modal-host >>> .kv-modal-body { padding: 16px; }
.edit-modal-flow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}
.edit-modal-head {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: center;
}
.edit-modal-head h2 {
    margin: 0;
    font-family: "Sora", sans-serif;
    font-size: 20px;
    font-weight: 400;
    color: #1f2337;
    line-height: 1.3;
}
.edit-modal-head p {
    margin: 0;
    font-size: 14px;
    color: #626473;
    line-height: 1.3;
}
.edit-modal-flow .field { width: 100%; text-align: left; }
.edit-modal-footer {
    width: 100%;
    display: flex;
    gap: 8px;
}

@media (max-width: 700px) {
    .edit-page {
        min-height: calc(100dvh - 50px);
        margin: 0;
        padding: 0;
        background: #fff;
    }
    .sheet {
        width: 100%;
        min-height: calc(100dvh - 50px);
        border: 0;
        border-radius: 0;
    }
    .edit-body { padding-bottom: 108px; }
    .footer {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        margin: 0;
        z-index: 6;
        padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
    }
    .edit-modal-host >>> .kv-modal-wrapper.kv-modal-small {
        max-width: 100%;
        width: 95%;
    }
    .password-head { display: none; }
}
</style>
